import { motion, useInView } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import TeamSectionPage from './team-card';
import { AnimatedButton } from '@/components/common/animated-button';
import { trackOutboundLink } from '@/lib/analytics';

const stats = [
    {
        value: "100000",
        suffix: "+",
        label: "Scans Processed",
    },
    {
        value: "99.9",
        suffix: "%",
        label: "Uptime",
    },
    {
        value: "100",
        suffix: "+",
        label: "Radiologists",
    }
];

// Counter component
function Counter({ value, suffix = "", isInView, delay }: { value: string; suffix?: string; isInView: boolean; delay: number }) {
    const [displayValue, setDisplayValue] = useState("0");
    const hasDecimal = value.includes('.');

    useEffect(() => {
        if (!isInView) return;

        const numericValue = parseFloat(value);
        if (isNaN(numericValue)) return;

        let currentValue = 0;
        const increment = numericValue / 60;

        const timer = setTimeout(() => {
            const interval = setInterval(() => {
                currentValue += increment;
                if (currentValue >= numericValue) {
                    setDisplayValue(hasDecimal ? numericValue.toFixed(1) : String(Math.round(numericValue)));
                    clearInterval(interval);
                } else {
                    setDisplayValue(hasDecimal ? currentValue.toFixed(1) : String(Math.round(currentValue)));
                }
            }, 20);
        }, delay * 1000);

        return () => clearTimeout(timer);
    }, [isInView, value, delay, hasDecimal]);

    return (
        <span className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground">
            {displayValue}{suffix}
        </span>
    );
}

// Traction Section - combines badges and stats in a clean layout
function TractionSection() {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
        <motion.section
            ref={ref}
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="relative w-full py-24 px-6"
        >
            <div className="max-w-5xl mx-auto">
                {/* Badges row - subtle, inline */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6 }}
                    className="flex flex-wrap items-center justify-center gap-6 mb-16"
                >
                    <a
                        href="https://www.nvidia.com/en-in/startups/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-[#76B900]/30 bg-[#76B900]/5 hover:bg-[#76B900]/10 transition-colors"
                        onClick={() => trackOutboundLink('nvidia_inception')}
                    >
                        {/* NVIDIA Eye Logo */}
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#76B900">
                            <path d="M11.424.5c-.042.02-.135.064-.207.098-.88.42-1.588 1.255-2.04 2.406-.193.49-.357 1.072-.47 1.67l-.016.084-.073-.006c-1.09-.083-2.02.07-2.824.464-1.016.5-1.73 1.33-2.102 2.447-.126.378-.21.78-.264 1.268l-.012.11-.078.016c-.634.127-1.157.334-1.614.638-.69.46-1.154 1.088-1.42 1.922-.08.25-.143.546-.19.913-.035.278-.046.433-.046.746 0 .432.03.766.1 1.12.245 1.24.966 2.218 2.148 2.913.395.232.83.407 1.36.548l.134.035.003.1c.014.543.083 1.02.22 1.51.37 1.32 1.198 2.346 2.444 3.027.536.293 1.133.498 1.872.643l.19.037.02.067c.19.637.468 1.19.854 1.7.685.907 1.638 1.536 2.833 1.868.426.12.867.19 1.418.226.19.012.7.006.9-.01 1.074-.09 1.962-.373 2.738-.873.956-.616 1.656-1.496 2.062-2.593.104-.28.18-.55.252-.9l.03-.14.11-.02c.693-.123 1.283-.322 1.823-.616 1.1-.6 1.87-1.506 2.276-2.68.115-.33.19-.664.247-1.1.034-.262.045-.42.045-.73 0-.434-.03-.764-.1-1.114-.25-1.244-.973-2.226-2.162-2.922-.4-.234-.838-.41-1.376-.553l-.13-.034-.003-.095c-.02-.62-.1-1.158-.26-1.712-.394-1.367-1.26-2.422-2.566-3.122-.51-.273-1.082-.467-1.784-.604l-.17-.033-.026-.092c-.2-.69-.498-1.29-.925-1.854C14.843 1.16 13.88.5 12.66.5c-.178 0-.282.004-.454.026-.27.034-.498.084-.782.174z"/>
                        </svg>
                        <span className="text-sm text-foreground/90 font-medium">NVIDIA Inception</span>
                    </a>
                    <div className="flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-primary/30 bg-primary/5">
                        <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm text-foreground/90 font-medium">Live & Commercialized</span>
                    </div>
                </motion.div>

                {/* Stats - horizontal, compact */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 md:gap-16">
                    {stats.map((stat, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                            transition={{ duration: 0.5, delay: 0.2 + (index * 0.1) }}
                            className={`text-center ${index === 2 ? 'sm:col-span-1 col-span-2 mx-auto sm:mx-0' : ''}`}
                        >
                            <Counter
                                value={stat.value}
                                suffix={stat.suffix}
                                isInView={isInView}
                                delay={0.3 + (index * 0.1)}
                            />
                            <p className="text-sm text-foreground/50 font-light mt-1 uppercase tracking-wider">
                                {stat.label}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </motion.section>
    );
}

function TeamSection() {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
        <motion.section
            ref={ref}
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="py-24 px-6 relative"
            id="team"
        >
            <div className="max-w-5xl mx-auto">
                <TeamSectionPage />
            </div>
        </motion.section>
    );
}

function CTASection() {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });

    return (
        <motion.section
            ref={ref}
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.8 }}
            className="py-24 px-6 relative"
        >
            <div className="max-w-4xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                    transition={{ duration: 0.6 }}
                    className="text-center p-12 rounded-2xl border border-border/50 bg-white/[0.02]"
                >
                    <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-foreground mb-4">
                        Ready to Modernize Your Workflow?
                    </h2>
                    <p className="text-base md:text-lg text-foreground/60 max-w-xl mx-auto mb-8">
                        Join diagnostic centers across India already using Somatiq.
                    </p>
                    <div className='flex justify-center'>
                    <AnimatedButton trackingSource="about_cta_demo">
                        SCHEDULE A DEMO
                    </AnimatedButton>
                    </div>
                </motion.div>
            </div>
        </motion.section>
    );
}

export default function FeatureSection() {
    return (
        <div>
            <TractionSection />
            <TeamSection />
            <CTASection />
        </div>
    );
}
